<template>
  <transition-group tag="div" name="sherpa-toast-fade" class="sherpa-toast-stack">
    <div
      v-for="t in state.toasts"
      :key="t.id"
      :class="['sherpa-toast', 'sherpa-toast--' + t.type]"
      :style="t.duration > 0 ? { '--toast-duration': t.duration + 'ms' } : null"
      role="alert"
    >
      <i :class="['sherpa-toast__icon', 'sherpa-icon', iconClass(t.type)]"></i>
      <div class="sherpa-toast__body">
        <div class="sherpa-toast__text" v-html="t.message"></div>
      </div>
      <button class="sherpa-toast__close" type="button" title="Cerrar" aria-label="Cerrar" @click="onDismiss(t.id)">
        <svg v-if="t.duration > 0" class="sherpa-toast__ring" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="10"></circle>
        </svg>
        <span class="sherpa-toast__x">&times;</span>
      </button>
    </div>
  </transition-group>
</template>

<script>
export default {
  name: 'sherpa-toast-host',
  props: {
    state: { type: Object, required: true },
    onDismiss: { type: Function, required: true }
  },
  methods: {
    iconClass (type) {
      if (type === 'success') return 'icon-ico_ok'
      if (type === 'error') return 'icon-ico_error'
      return 'icon-ico_error'
    }
  }
}
</script>

<style>
/* ============ SHERPA TOAST ============ */
.sherpa-toast-stack {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 100000001;
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;            /* los clics pasan salvo sobre la tarjeta */
}

.sherpa-toast {
  pointer-events: auto;
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 330px;
  max-width: 440px;
  padding: 15px 44px 15px 16px;    /* 44px derecha: hueco del botón cerrar */
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-left: 4px solid #b8b8b8;  /* color sobrescrito por el modificador de tipo */
  border-radius: 6px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  font-family: "Source Sans Pro", sans-serif;
  box-sizing: border-box;
}

/* --- Icono --- */
.sherpa-toast__icon {
  flex: 0 0 auto;
  font-size: 20px;
  line-height: 1.35;
  position: relative;
  top: 1px;
}

/* --- Texto --- */
.sherpa-toast__body { flex: 1 1 auto; min-width: 0; }
.sherpa-toast__text {
  font-size: 14px;
  font-weight: 400;
  color: #554b5a;                  /* $mortar */
  line-height: 1.45;
  word-break: break-word;
}
.sherpa-toast__text p,
.sherpa-toast__text ul,
.sherpa-toast__text ol { margin: 4px 0 0; }
.sherpa-toast__text a { color: #21b7c9; }

/* --- Botón cerrar con anillo de cuenta atrás --- */
.sherpa-toast__close {
  position: absolute;
  top: 9px;
  right: 10px;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b8b8b8;
  transition: color 0.15s ease-in;
}
.sherpa-toast__close:hover { color: #1f2933; }
.sherpa-toast__x {
  position: relative;
  z-index: 1;
  font-size: 17px;
  line-height: 1;
}
.sherpa-toast__ring {
  position: absolute;
  inset: 0;
  width: 22px;
  height: 22px;
  transform: rotate(-90deg);       /* arranca arriba y se vacía en sentido horario */
  pointer-events: none;
}
.sherpa-toast__ring circle {
  fill: none;
  stroke: #c9c9c9;                 /* sobrescrito por el modificador de tipo */
  stroke-width: 2;
  stroke-linecap: round;
  stroke-dasharray: 62.83;         /* 2·π·10 */
  stroke-dashoffset: 0;
  opacity: 0.6;
  animation: sherpaToastCountdown var(--toast-duration, 3000ms) linear forwards;
}
@keyframes sherpaToastCountdown {
  to { stroke-dashoffset: 62.83; } /* el anillo se vacía a medida que corre el tiempo */
}

/* --- Modificadores por tipo (barra + icono + anillo) --- */
.sherpa-toast--success { border-left-color: #27a556; }
.sherpa-toast--success .sherpa-toast__icon { color: #27a556; }
.sherpa-toast--success .sherpa-toast__ring circle { stroke: #27a556; }

.sherpa-toast--error { border-left-color: #cf0000; }
.sherpa-toast--error .sherpa-toast__icon { color: #cf0000; }
.sherpa-toast--error .sherpa-toast__ring circle { stroke: #cf0000; }

.sherpa-toast--warning { border-left-color: #f59e0b; }
.sherpa-toast--warning .sherpa-toast__icon { color: #f59e0b; }
.sherpa-toast--warning .sherpa-toast__ring circle { stroke: #f59e0b; }

.sherpa-toast--info { border-left-color: #21b7c9; }
.sherpa-toast--info .sherpa-toast__icon { color: #21b7c9; }
.sherpa-toast--info .sherpa-toast__ring circle { stroke: #21b7c9; }

/* ============ ANIMACIÓN entrada/salida ============ */
.sherpa-toast-fade-enter-from {
  opacity: 0;
  transform: translateX(24px);
}
.sherpa-toast-fade-enter-active {
  transition: opacity 0.22s cubic-bezier(0.22, 1, 0.36, 1),
              transform 0.22s cubic-bezier(0.22, 1, 0.36, 1);
}
.sherpa-toast-fade-enter-to { opacity: 1; transform: translateX(0); }

.sherpa-toast-fade-leave-from { opacity: 1; transform: translateX(0); }
.sherpa-toast-fade-leave-active {
  transition: opacity 0.18s cubic-bezier(0.4, 0, 1, 1),
              transform 0.18s cubic-bezier(0.4, 0, 1, 1);
  position: absolute;
  right: 0;
}
.sherpa-toast-fade-leave-to { opacity: 0; transform: translateX(24px); }

.sherpa-toast-fade-move { transition: transform 0.2s ease; }
</style>
